import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { Copy, Check, AlertCircle, Loader2 } from 'lucide-react';

export interface QRCodeComponentProps {
  value: string;
  size?: number;
  label?: string;
  className?: string;
  showCopyButton?: boolean;
}

export const QRCodeComponent: React.FC<QRCodeComponentProps> = ({
  value,
  size = 150,
  label,
  className = '',
  showCopyButton = false
}) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!value) {
      setError('No payment link available');
      return;
    }

    generateQRCode();
  }, [value, size]);

  const generateQRCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      const dataUrl = await QRCode.toDataURL(value, {
        errorCorrectionLevel: 'M',
        type: 'image/png',
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        width: size
      });
      
      setQrDataUrl(dataUrl);
    } catch (err) {
      console.error('QR Code generation error:', err);
      setError('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  if (!value) {
    return (
      <div 
        data-testid="qr-code-container"
        className={`flex flex-col items-center p-4 bg-gray-50 rounded-lg ${className}`}
      >
        <AlertCircle className="h-8 w-8 text-gray-400 mb-2" />
        <p className="text-sm text-gray-600 text-center">No payment link available</p>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        data-testid="qr-code-container"
        className={`flex flex-col items-center p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}
      >
        <AlertCircle className="h-8 w-8 text-red-400 mb-2" />
        <p className="text-sm text-red-600 text-center">{error}</p>
      </div>
    );
  }

  return (
    <div 
      data-testid="qr-code-container"
      className={`flex flex-col items-center p-4 bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}
    >
      {/* QR Code Display */}
      <div className="relative mb-3">
        {loading ? (
          <div 
            className="flex items-center justify-center bg-gray-100 rounded-lg"
            style={{ width: size, height: size }}
          >
            <div className="flex flex-col items-center">
              <Loader2 className="h-6 w-6 text-blue-600 animate-spin mb-2" />
              <p className="text-xs text-gray-600">Generating QR Code...</p>
            </div>
          </div>
        ) : qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt="QR Code for payment"
            className="rounded-lg border border-gray-100"
            style={{ width: size, height: size }}
          />
        ) : null}
      </div>

      {/* Label */}
      <div className="text-center mb-3">
        <p className="text-sm font-medium text-gray-900 mb-1">
          {label || 'Scan to Pay'}
        </p>
        <p className="text-xs text-gray-600">
          Point your phone camera at the QR code
        </p>
      </div>

      {/* Copy Button */}
      {showCopyButton && (
        <button
          onClick={handleCopyLink}
          className="flex items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 mr-1 text-green-600" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="h-3 w-3 mr-1" />
              Copy Link
            </>
          )}
        </button>
      )}
    </div>
  );
};
